/**
 * PetCroc Mega Menu - Vanilla JS Web Component
 * Gerencia abertura, fechamento, troca de abas com debounce e proteção contra troca acidental,
 * acessibilidade por teclado (Esc, Enter, Space) e controle inteligente de vídeo.
 */

class PetcrocMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.activeTabId = 'tab-brinquedos';
    this.hoverTimeout = null;
    this.closeTimeout = null;
    this.circleHoverTimeout = null;
    this.closeDelay = parseInt(this.getAttribute('data-close-delay') || '180', 10);
    this.hoverDebounce = 150;
    this.circleDwellDelay = 220; // Tempo de pausa intencional no círculo para trocar de aba
    this.transitLockUntil = 0;   // Janela de proteção contra passagem acidental do mouse
  }

  connectedCallback() {
    this.triggerBtn = this.querySelector('#petcroc-all-categories-btn');
    this.panel = this.querySelector('#petcroc-mega-menu-panel');
    this.shortcuts = this.querySelectorAll('.petcroc-mega-nav__shortcut-link');
    this.tabButtons = this.querySelectorAll('.petcroc-mega-tab-btn');
    this.tabPanes = this.querySelectorAll('.petcroc-tab-pane');
    this.contentWrap = this.querySelector('.petcroc-mega-panel__content-wrap');

    if (!this.panel) return;

    this.bindEvents();
  }

  bindEvents() {
    // 1. Botão "Todas as categorias"
    if (this.triggerBtn) {
      this.triggerBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMenu();
      });

      this.triggerBtn.addEventListener('mouseenter', () => {
        this.scheduleOpen('tab-brinquedos');
      });
    }

    // 2. Atalhos diretos da barra superior
    this.shortcuts.forEach((link) => {
      const targetTab = link.getAttribute('data-tab-target');

      link.addEventListener('mouseenter', () => {
        this.scheduleOpen(targetTab);
      });

      link.addEventListener('focus', () => {
        this.scheduleOpen(targetTab);
      });
    });

    // 3. Abas circulares superiores (com proteção contra passagem acidental do mouse)
    this.tabButtons.forEach((btn) => {
      const tabId = btn.getAttribute('data-tab-id');

      btn.addEventListener('mouseenter', () => {
        // Se o usuário acabou de entrar por um atalho e está descendo o mouse, não troca acidentalmente
        if (Date.now() < this.transitLockUntil) {
          return;
        }

        if (this.circleHoverTimeout) {
          clearTimeout(this.circleHoverTimeout);
        }

        // Exige permanência intencional (dwell time) para trocar de aba ao passar o mouse
        this.circleHoverTimeout = setTimeout(() => {
          this.switchTab(tabId);
        }, this.circleDwellDelay);
      });

      btn.addEventListener('mouseleave', () => {
        if (this.circleHoverTimeout) {
          clearTimeout(this.circleHoverTimeout);
          this.circleHoverTimeout = null;
        }
      });

      // Clique no círculo é uma intenção explícita e troca imediatamente
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.circleHoverTimeout) {
          clearTimeout(this.circleHoverTimeout);
          this.circleHoverTimeout = null;
        }
        this.transitLockUntil = 0;
        this.switchTab(tabId);
      });

      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.switchTab(tabId);
        }
      });
    });

    // 4. Quando o cursor entra na área de conteúdo (links/colunas), cancela qualquer troca pendente de círculo
    if (this.contentWrap) {
      this.contentWrap.addEventListener('mouseenter', () => {
        if (this.circleHoverTimeout) {
          clearTimeout(this.circleHoverTimeout);
          this.circleHoverTimeout = null;
        }
      });
    }

    // 5. Manter aberto ao passar o mouse e fechar com delay ao sair
    this.addEventListener('mouseenter', () => {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
    });

    this.addEventListener('mouseleave', () => {
      if (this.circleHoverTimeout) {
        clearTimeout(this.circleHoverTimeout);
        this.circleHoverTimeout = null;
      }
      this.scheduleClose();
    });

    // 6. Fechar com a tecla ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeMenu();
        if (this.triggerBtn) this.triggerBtn.focus();
      }
    });

    // 7. Fechar ao clicar fora
    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.contains(e.target)) {
        this.closeMenu();
      }
    });
  }

  scheduleOpen(targetTabId) {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }

    this.hoverTimeout = setTimeout(() => {
      this.openMenu(targetTabId);
    }, this.hoverDebounce);
  }

  scheduleClose() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }

    this.closeTimeout = setTimeout(() => {
      this.closeMenu();
    }, this.closeDelay);
  }

  toggleMenu() {
    if (this.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu(this.activeTabId || 'tab-brinquedos');
    }
  }

  openMenu(tabId) {
    this.isOpen = true;
    this.classList.add('is-open');

    // Ao abrir por um atalho, ativa janela de proteção (450ms) para o mouse descer sem ser desviado por outros círculos
    this.transitLockUntil = Date.now() + 450;

    if (this.circleHoverTimeout) {
      clearTimeout(this.circleHoverTimeout);
      this.circleHoverTimeout = null;
    }

    if (this.triggerBtn) {
      this.triggerBtn.setAttribute('aria-expanded', 'true');
    }

    if (this.panel) {
      this.panel.setAttribute('aria-hidden', 'false');
    }

    if (tabId) {
      this.switchTab(tabId);
    } else {
      this.handleVideos(true);
    }
  }

  closeMenu() {
    this.isOpen = false;
    this.classList.remove('is-open');
    this.transitLockUntil = 0;

    if (this.circleHoverTimeout) {
      clearTimeout(this.circleHoverTimeout);
      this.circleHoverTimeout = null;
    }

    if (this.triggerBtn) {
      this.triggerBtn.setAttribute('aria-expanded', 'false');
    }

    if (this.panel) {
      this.panel.setAttribute('aria-hidden', 'true');
    }

    this.shortcuts.forEach((link) => link.classList.remove('is-active'));
    this.handleVideos(false);
  }

  switchTab(tabId) {
    this.activeTabId = tabId;

    // Atualiza botões das abas circulares
    this.tabButtons.forEach((btn) => {
      const match = btn.getAttribute('data-tab-id') === tabId;
      btn.classList.toggle('is-active', match);
      btn.setAttribute('aria-selected', match ? 'true' : 'false');
    });

    // Atualiza atalhos da barra
    this.shortcuts.forEach((link) => {
      const match = link.getAttribute('data-tab-target') === tabId;
      link.classList.toggle('is-active', match);
    });

    // Atualiza painéis
    this.tabPanes.forEach((pane) => {
      const paneId = pane.id;
      const targetPaneId = `petcroc-tab-panel-${tabId.replace('tab-', '')}`;
      const match = paneId === targetPaneId;

      if (match) {
        pane.classList.add('is-active');
        pane.removeAttribute('hidden');
      } else {
        pane.classList.remove('is-active');
        pane.setAttribute('hidden', '');
      }
    });

    this.handleVideos(this.isOpen);
  }

  handleVideos(shouldPlay) {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const allVideos = this.querySelectorAll('video');

    allVideos.forEach((vid) => {
      if (!vid) return;

      const isInsideActiveTab = vid.closest('.petcroc-tab-pane.is-active') !== null;

      if (shouldPlay && isInsideActiveTab && !prefersReducedMotion) {
        if (vid.preload === 'none') {
          vid.preload = 'auto';
        }
        vid.play().catch(() => {});
      } else {
        vid.pause();
      }
    });
  }
}

if (!customElements.get('petcroc-mega-menu')) {
  customElements.define('petcroc-mega-menu', PetcrocMegaMenu);
}
